import { useEffect, useMemo, useState } from "react";
import { Plus, Calendar, Trash2, Pencil, Video, Phone, Users, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import Pager from "@/components/site/Pager";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["scheduled", "done", "rescheduled", "no_show", "cancelled"];
const STATUS_COLORS = {
  scheduled: "bg-blue-100 text-blue-700",
  done: "bg-emerald-100 text-emerald-700",
  rescheduled: "bg-amber-100 text-amber-700",
  no_show: "bg-rose-100 text-rose-700",
  cancelled: "bg-slate-100 text-slate-600",
};
const MODES = ["video", "phone", "in_person"];
const MODE_ICONS = { video: Video, phone: Phone, in_person: Users };
const PAGE_SIZE = 20;

export default function AdminInterviews() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusF, setStatusF] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/admin/interviews", { params: { status: statusF || undefined, page, page_size: PAGE_SIZE } })
    .then((r) => { setItems(r.data.items || []); setTotal(Number(r.data.total) || 0); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusF, page]);
  useEffect(() => { setPage(1); }, [statusF]);

  const remove = async (id) => {
    if (!window.confirm("Delete this interview?")) return;
    await api.delete(`/admin/interviews/${id}`); toast.success("Deleted"); load();
  };

  const grouped = useMemo(() => {
    const map = {};
    items.forEach((iv) => {
      const day = new Date(iv.scheduled_at).toDateString();
      (map[day] = map[day] || []).push(iv);
    });
    return map;
  }, [items]);

  return (
    <div className="space-y-6" data-testid="admin-interviews">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Interviews</h1>
          <p className="text-sm text-slate-500">{total} interviews · schedule + feedback</p>
        </div>
        <Button onClick={() => setCreating(true)} data-testid="interview-new"><Plus className="mr-1 h-4 w-4" />Schedule interview</Button>
      </div>

      <div className="flex items-center gap-2">
        <Select value={statusF || "all"} onValueChange={(v) => setStatusF(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48" data-testid="interviews-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-6">
        {Object.keys(grouped).length === 0 && <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center text-slate-400">No interviews scheduled.</div>}
        {Object.entries(grouped).map(([day, ivs]) => (
          <div key={day}>
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
              <Calendar className="h-4 w-4 text-primary" /> {day}
              <span className="text-xs font-normal text-slate-400">· {ivs.length} scheduled</span>
            </div>
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                  <tr><th className="px-4 py-3">Time</th><th className="px-4 py-3">Candidate</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Round</th><th className="px-4 py-3">Mode</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
                </thead>
                <tbody>
                  {ivs.map((iv) => {
                    const MI = MODE_ICONS[iv.mode] || Video;
                    return (
                      <tr key={iv.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`interview-${iv.id}`}>
                        <td className="px-4 py-3 font-medium text-slate-900">{new Date(iv.scheduled_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</td>
                        <td className="px-4 py-3 text-slate-700">
                          <div>{iv.candidate_name || "—"}</div>
                          <div className="text-xs text-slate-500">{iv.candidate_email}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-700">{iv.job_title || "—"}</td>
                        <td className="px-4 py-3 text-slate-700">{iv.round_name}</td>
                        <td className="px-4 py-3 text-slate-700"><span className="inline-flex items-center gap-1"><MI className="h-3.5 w-3.5 text-slate-500" />{iv.mode.replace("_", " ")}</span></td>
                        <td className="px-4 py-3"><Badge className={STATUS_COLORS[iv.status]}>{iv.status.replace("_", " ")}</Badge></td>
                        <td className="px-4 py-3 text-right">
                          <Button variant="ghost" size="sm" onClick={() => setEditing(iv)}><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => remove(iv.id)}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      <Pager page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} testIdPrefix="admin-interviews-pager" />

      {creating && <ScheduleDialog onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {editing && <EditDialog value={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ScheduleDialog({ onClose, onSaved }) {
  const [apps, setApps] = useState([]);
  const [f, setF] = useState({
    application_id: "", round_name: "Screening", mode: "video",
    scheduled_at: new Date().toISOString().slice(0, 16),
    duration_min: 30, panelist_name: "", panelist_email: "", location_or_link: "",
  });
  useEffect(() => { api.get("/admin/applications", { params: { page_size: 200 } }).then((r) => setApps(r.data.items || [])); }, []);
  const save = async () => {
    if (!f.application_id) { toast.error("Select a candidate"); return; }
    try {
      const payload = { ...f, scheduled_at: new Date(f.scheduled_at).toISOString() };
      await api.post("/admin/interviews", payload); toast.success("Scheduled"); onSaved();
    } catch { toast.error("Failed"); }
  };
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>Schedule interview</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <Select value={f.application_id} onValueChange={(v) => setF({ ...f, application_id: v })}>
            <SelectTrigger data-testid="interview-candidate"><SelectValue placeholder="Select candidate" /></SelectTrigger>
            <SelectContent className="max-h-80">{apps.map((a) => <SelectItem key={a.id} value={a.id}>{a.candidate_name} — {a.job_title || "General"}</SelectItem>)}</SelectContent>
          </Select>
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Round (e.g. HR / Technical)" value={f.round_name} onChange={(e) => setF({ ...f, round_name: e.target.value })} />
            <Select value={f.mode} onValueChange={(v) => setF({ ...f, mode: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m.replace("_", " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input type="datetime-local" value={f.scheduled_at} onChange={(e) => setF({ ...f, scheduled_at: e.target.value })} data-testid="interview-date" />
            <Input type="number" placeholder="Duration (min)" value={f.duration_min} onChange={(e) => setF({ ...f, duration_min: parseInt(e.target.value) || 30 })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Panelist name" value={f.panelist_name} onChange={(e) => setF({ ...f, panelist_name: e.target.value })} />
            <Input placeholder="Panelist email" value={f.panelist_email} onChange={(e) => setF({ ...f, panelist_email: e.target.value })} />
          </div>
          <Input placeholder="Meeting link / address" value={f.location_or_link} onChange={(e) => setF({ ...f, location_or_link: e.target.value })} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} data-testid="interview-save">Schedule</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StarRating({ v, onChange, label }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-slate-600">{label}</span>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => onChange(n)}>
            <Star className={`h-4 w-4 ${n <= v ? "fill-yellow-400 text-yellow-400" : "text-slate-300"}`} />
          </button>
        ))}
      </div>
    </div>
  );
}

function EditDialog({ value, onClose, onSaved }) {
  const [f, setF] = useState({ ...value, scheduled_at: value.scheduled_at?.slice(0, 16) || "" });
  const save = async () => {
    const payload = { ...f };
    if (payload.scheduled_at) payload.scheduled_at = new Date(payload.scheduled_at).toISOString();
    try { await api.put(`/admin/interviews/${value.id}`, payload); toast.success("Updated"); onSaved(); }
    catch { toast.error("Failed"); }
  };
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>Update interview — {value.candidate_name}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Round" value={f.round_name} onChange={(e) => setF({ ...f, round_name: e.target.value })} />
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger data-testid="interview-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Input type="datetime-local" value={f.scheduled_at} onChange={(e) => setF({ ...f, scheduled_at: e.target.value })} />
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-500">Feedback scorecard</div>
            <div className="space-y-2">
              <StarRating v={f.feedback_tech} onChange={(v) => setF({ ...f, feedback_tech: v })} label="Technical" />
              <StarRating v={f.feedback_culture} onChange={(v) => setF({ ...f, feedback_culture: v })} label="Cultural fit" />
              <StarRating v={f.feedback_comm} onChange={(v) => setF({ ...f, feedback_comm: v })} label="Communication" />
            </div>
          </div>
          <Textarea placeholder="Interviewer comments" value={f.feedback_notes} onChange={(e) => setF({ ...f, feedback_notes: e.target.value })} rows={3} />
          <Input placeholder="Next round (optional)" value={f.next_round} onChange={(e) => setF({ ...f, next_round: e.target.value })} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} data-testid="interview-update-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
